/**
 * <whats-included>
 *
 * Swaps the "What's included" image to match the selected variant. The image is
 * driven by each variant's `custom.whats_inside_image` metafield (rendered by
 * Liquid into a variant-id → url map); on colour change it updates the <img>.
 *
 * Detection mirrors the other variant-reactive elements on the PDP: read the
 * product form's [name="id"], react to the theme's option-change pubsub event
 * and to change events from <variant-selects>, with a light poll as a backstop.
 * Safe fallback: if a variant has no metafield the map holds the section default,
 * and a null entry simply leaves the current image in place.
 */
if (!customElements.get('whats-included')) {
  customElements.define(
    'whats-included',
    class WhatsIncluded extends HTMLElement {
      connectedCallback() {
        this.img = this.querySelector('[data-wi-image]');
        this.productId = this.dataset.productId;

        try {
          this.map = JSON.parse(this.querySelector('[data-wi-map]')?.textContent || '{}');
        } catch (e) {
          this.map = {};
        }

        this.refresh = this.refresh.bind(this);
        this._lastId = this.currentVariantId();
        this.refresh();

        // React to <variant-selects> changes (radios/selects bubble to document).
        this._onChange = (e) => {
          if (e.target.closest && e.target.closest('variant-selects')) {
            setTimeout(this.refresh, 0);
          }
        };
        document.addEventListener('change', this._onChange, true);

        // React to the theme's option-selection pubsub, when available.
        const pubsub = window.FoxTheme && window.FoxTheme.pubsub;
        if (pubsub && pubsub.PUB_SUB_EVENTS && pubsub.PUB_SUB_EVENTS.optionValueSelectionChange) {
          this._unsub = pubsub.subscribe(pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, () =>
            setTimeout(this.refresh, 0)
          );
        }

        // Backstop poll for anything the above miss (opaque widgets, async updates).
        this._timer = setInterval(() => {
          const id = this.currentVariantId();
          if (id !== this._lastId) {
            this._lastId = id;
            this.refresh();
          }
        }, 400);
      }

      disconnectedCallback() {
        clearInterval(this._timer);
        document.removeEventListener('change', this._onChange, true);
        if (typeof this._unsub === 'function') this._unsub();
      }

      /** Current variant id from the product form (or the first mapped id). */
      currentVariantId() {
        const input =
          document.querySelector(`form[data-product-id="${this.productId}"] [name="id"]`) ||
          document.querySelector('form[is="product-form"] [name="id"]') ||
          document.querySelector('[name="id"]');
        return input ? String(input.value) : Object.keys(this.map)[0] || null;
      }

      refresh() {
        if (!this.img) return;
        const id = this.currentVariantId();
        const url = id ? this.map[id] : null;
        if (!url) return; // no image for this variant → keep the current one
        if (this.img.tagName === 'IMG') {
          if (this.img.getAttribute('src') !== url) {
            this.img.removeAttribute('srcset');
            this.img.src = url;
          }
        }
      }
    }
  );
}
